import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { can } from "@/lib/roles";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Plus, Loader2, ClipboardList, Check, PenLine } from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { MonthlyFinancials } from "@/components/MonthlyFinancials";
import { LateReportScoreboard } from "@/components/LateReportScoreboard";

const todayStr = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};
const addDays = (ds, n) => {
  const d = new Date(ds + "T00:00:00");
  d.setDate(d.getDate() + n);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};
const chipLabel = (ds) => {
  const d = new Date(ds + "T00:00:00");
  return {
    weekday: d.toLocaleDateString("en-US", { weekday: "short" }),
    date: d.toLocaleDateString("en-US", { month: "short", day: "numeric" }),
  };
};

export default function DailyReports() {
  const { user } = useAuth();
  const [reports, setReports] = useState(null);
  const [props, setProps] = useState([]);
  const [filter, setFilter] = useState("all");
  const [tab, setTab] = useState("daily");
  const [monthProp, setMonthProp] = useState("");

  const canCreate = can(user, ["admin", "regional", "gm", "finance"]);
  const canScore = can(user, ["admin", "regional"]);

  useEffect(() => {
    api.get("/properties").then((r) => {
      setProps(r.data);
      if (r.data[0]) setMonthProp((cur) => cur || r.data[0].id);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    const q = filter === "all" ? "" : `?property_id=${filter}`;
    api.get(`/daily-reports${q}`).then((r) => setReports(r.data)).catch(() => setReports([]));
  }, [filter]);

  // group reports per property -> filled dates + last filled (on/before today; ignore future-dated)
  const t0 = todayStr();
  const byProp = {};
  (reports || []).forEach((r) => {
    const g = byProp[r.property_id] || (byProp[r.property_id] = { filled: {}, last: null });
    g.filled[r.report_date] = r.status;
    if (r.report_date <= t0 && (!g.last || r.report_date > g.last)) g.last = r.report_date;
  });

  const visibleProps = filter === "all" ? props : props.filter((p) => p.id === filter);

  const DateChip = ({ propertyId, date, status }) => {
    const { weekday, date: dlabel } = chipLabel(date);
    const filled = !!status;
    return (
      <Link
        to={`/daily-reports/${propertyId}/${date}`}
        data-testid={`date-chip-${propertyId}-${date}`}
        className={`group flex min-w-[110px] flex-col border px-4 py-2.5 transition-colors ${
          filled
            ? "border-primary/40 bg-primary/5 hover:bg-primary/10"
            : "border-dashed border-border hover:border-primary hover:bg-secondary"
        }`}
      >
        <span className="label-caps text-[0.6rem] text-muted-foreground">{weekday}</span>
        <span className="font-heading text-sm font-bold">{dlabel}</span>
        <span className={`mt-0.5 flex items-center gap-1 text-[0.65rem] font-medium ${filled ? "text-primary" : "text-muted-foreground"}`}>
          {filled ? (<><Check className="h-3 w-3" /> {status}</>) : (<><PenLine className="h-3 w-3" /> Enter data</>)}
        </span>
      </Link>
    );
  };

  return (
    <div className="space-y-8" data-testid="daily-reports-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="label-caps text-primary">Operations</div>
          <h1 className="font-heading text-3xl font-black tracking-tight lg:text-4xl">Daily Report Entry</h1>
          <p className="mt-1 text-sm text-muted-foreground">Pick a day to enter or review. Each property shows its last filled day and the next two days.</p>
        </div>
        {tab === "daily" && (
          <div className="flex items-center gap-2">
            <Select value={filter} onValueChange={setFilter}>
              <SelectTrigger className="w-48" data-testid="report-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All properties</SelectItem>
                {props.map((p) => (
                  <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {canCreate && (
              <Button asChild variant="outline" data-testid="new-report-btn">
                <Link to="/daily-reports/new"><Plus className="mr-2 h-4 w-4" /> Other Date</Link>
              </Button>
            )}
          </div>
        )}
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList data-testid="daily-reports-tabs">
          <TabsTrigger value="daily" data-testid="tab-daily-entry">Daily Entry</TabsTrigger>
          <TabsTrigger value="monthly" data-testid="tab-monthly-entry">Monthly Entry (Prior Years)</TabsTrigger>
          {canScore && <TabsTrigger value="scoreboard" data-testid="tab-scoreboard">Late Report Scoreboard</TabsTrigger>}
        </TabsList>

        {canScore && (
          <TabsContent value="scoreboard" className="mt-6"><LateReportScoreboard /></TabsContent>
        )}

        <TabsContent value="daily" className="mt-6">
          {reports === null ? (
            <div className="flex h-40 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
          ) : visibleProps.length === 0 ? (
            <Card className="rounded-none border p-12 text-center">
              <ClipboardList className="mx-auto h-8 w-8 text-muted-foreground" />
              <p className="mt-3 text-sm text-muted-foreground">No properties available.</p>
            </Card>
          ) : (
            <div className="space-y-3">
              {visibleProps.map((p) => {
                const g = byProp[p.id] || { filled: {}, last: null };
                const t = todayStr();
                const yest = addDays(t, -1); // you enter completed days only — never today
                // First day that still needs entry (day after last filled; or 3 days back if none filled)
                let start = g.last ? addDays(g.last, 1) : addDays(t, -3);
                // Never show today or future: cap the 3-day window so it ends at yesterday
                if (addDays(start, 2) > yest) start = addDays(t, -3);
                const dates = [start, addDays(start, 1), addDays(start, 2)];
                return (
                  <Card key={p.id} className="rounded-none border p-4" data-testid={`property-line-${p.id}`}>
                    <div className="flex flex-wrap items-center justify-between gap-4">
                      <div className="min-w-[180px]">
                        <div className="font-heading text-base font-bold">{p.name}</div>
                        <div className="text-xs text-muted-foreground">
                          {g.last ? `Last filled: ${chipLabel(g.last).date}` : "No entries yet"}
                        </div>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {dates.map((d) => (
                          <DateChip key={d} propertyId={p.id} date={d} status={g.filled[d]} />
                        ))}
                      </div>
                    </div>
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>

        <TabsContent value="monthly" className="mt-6">
          <Card className="rounded-none border p-6" data-testid="monthly-entry-tab">
            <div className="mb-4 flex flex-wrap items-center gap-3">
              <span className="text-sm font-medium">Property</span>
              <Select value={monthProp} onValueChange={setMonthProp}>
                <SelectTrigger className="w-64" data-testid="monthly-entry-property"><SelectValue placeholder="Select property" /></SelectTrigger>
                <SelectContent>
                  {props.map((p) => (<SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            <MonthlyFinancials propertyId={monthProp} canEdit={canCreate} />
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
